import React from "react";
import { mediaUrl } from "@/lib/api";
import { Link } from "react-router-dom";
import { MapPin, Mail, Globe, Instagram, Phone, MessageCircle } from "lucide-react";
import MagneticButton from "@/components/MagneticButton";
import { LOGO_URL } from "@/lib/brand";
import { PHONE_DISPLAY, PHONE_TEL, waLink } from "@/lib/contact";
import TodayHours from "@/components/store/TodayHours";

const WA_URL = waLink("Hello BIGA, I would like to place an order");
const MAPS_URL = "https://www.google.com/maps/search/?api=1&query=Ground+Floor+Shop+19+Elite+Elegance+Gotri+Sevasi+Road+Vadodara+390021";

const GROUPS = [
  {
    heading: "Order",
    links: [
      { to: "/catering", label: "Catering" },
      { to: "/gift-cards", label: "Gift Cards" },
      { to: "/biga-circle", label: "Biga Circle" },
      { to: "/nutrition", label: "Nutrition & Allergens" },
    ],
  },
  {
    heading: "Pizzeria",
    links: [
      { to: "/about", label: "About" },
      { to: "/faq", label: "FAQ" },
      { to: "/careers", label: "Careers" },
      { to: "/blog", label: "Journal" },
    ],
  },
  {
    heading: "Company",
    links: [
      { to: "/contact", label: "Contact" },
      { to: "/investors", label: "Investors" },
      { to: "/terms", label: "Terms", testid: "footer-terms-link" },
      { to: "/privacy", label: "Privacy", testid: "footer-privacy-link" },
    ],
  },
];

export default function Footer() {
  const scrollToMenu = () => document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" });
  return (
    <footer className="bg-biga-green text-biga-cream pt-20 sm:pt-24 pb-10 px-5 sm:px-8 relative overflow-hidden" data-testid="site-footer">
      <div className="max-w-6xl mx-auto">
        {/* Masthead */}
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-12 lg:gap-16 pb-14 border-b border-biga-cream/10">
          <div>
            <Link
              to="/"
              onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
              className="inline-block group"
              data-testid="footer-logomark-link"
            >
              <img
                src={mediaUrl(LOGO_URL, 320)}
                alt="BIGA Artisanal Pizzeria"
                width={200}
                height={100}
                loading="lazy"
                className="w-40 sm:w-48 h-auto transition-all duration-300 group-hover:brightness-[1.15]"
              />
            </Link>
            <p className="mt-6 text-biga-cream/70 font-medium text-base max-w-sm leading-relaxed">
              Whole Roman-style pizzas on 72-hour fermented biga dough, baked fresh in Vadodara.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <MagneticButton onClick={scrollToMenu} data-testid="footer-order-btn" className="liquid-btn bg-biga-orange text-biga-cream font-bold text-base px-8 py-3.5 rounded-full">
                Order Online
              </MagneticButton>
              <a href={WA_URL} target="_blank" rel="noreferrer" data-testid="footer-whatsapp" aria-label="Message BIGA on WhatsApp" className="inline-flex items-center gap-2 border border-biga-cream/20 text-biga-cream font-bold text-sm px-6 py-3.5 rounded-full hover:border-biga-orange hover:text-biga-orange transition-colors">
                <MessageCircle size={17} /> WhatsApp
              </a>
              <a href={PHONE_TEL} data-testid="footer-call" aria-label="Call BIGA" className="inline-flex items-center gap-2 border border-biga-cream/20 text-biga-cream font-bold text-sm px-6 py-3.5 rounded-full hover:border-biga-orange hover:text-biga-orange transition-colors">
                <Phone size={17} /> Call
              </a>
            </div>
          </div>

          {/* Tidy link groups */}
          <div className="grid grid-cols-3 gap-6 sm:gap-8">
            {GROUPS.map((g) => (
              <div key={g.heading}>
                <h3 className="font-display font-bold text-sm uppercase tracking-[0.2em] text-biga-orange/90 mb-4">{g.heading}</h3>
                <ul className="space-y-3">
                  {g.links.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        {...(l.testid ? { "data-testid": l.testid } : {})}
                        className="text-sm font-medium text-biga-cream/70 hover:text-biga-cream transition-colors"
                      >
                        {l.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Contact + compliance */}
        <div className="grid md:grid-cols-[1.1fr_0.9fr] gap-10 py-12 border-b border-biga-cream/10">
          <div className="space-y-3 text-sm font-medium text-biga-cream/70">
            <a href={MAPS_URL} target="_blank" rel="noreferrer" className="flex items-start gap-3 hover:text-biga-orange transition-colors" aria-label="Open BIGA location in Google Maps" data-testid="footer-maps">
              <MapPin size={17} className="mt-0.5 shrink-0 text-biga-orange/80" /> Ground Floor — Shop 19, Elite Elegance, Gotri Sevasi Road, Vadodara, Gujarat 390021
            </a>
            <a href={PHONE_TEL} className="flex items-center gap-3 hover:text-biga-orange transition-colors"><Phone size={17} className="text-biga-orange/80" /> {PHONE_DISPLAY}</a>
            <a href="mailto:info@bigapizzeria.in" className="flex items-center gap-3 hover:text-biga-orange transition-colors"><Mail size={17} className="text-biga-orange/80" /> info@bigapizzeria.in</a>
            <div className="flex items-center gap-5 pt-1">
              <a href="https://bigapizzeria.in" target="_blank" rel="noreferrer" aria-label="Visit bigapizzeria.in" className="flex items-center gap-2 hover:text-biga-orange transition-colors"><Globe size={17} className="text-biga-orange/80" /> bigapizzeria.in</a>
              <a href="https://www.instagram.com/biga.bdq" target="_blank" rel="noreferrer" aria-label="Follow BIGA on Instagram" data-testid="footer-instagram" className="flex items-center gap-2 hover:text-biga-orange transition-colors"><Instagram size={17} className="text-biga-orange/80" /> @biga.bdq</a>
            </div>
          </div>
          <div className="space-y-2 text-sm font-medium text-biga-cream/70 md:text-right" data-testid="fssai-block">
            <p><span className="font-black text-biga-cream tracking-wide">FSSAI Lic. No.</span> 20726032004121</p>
            <p>Free delivery on orders above ₹799, flat ₹40 otherwise.</p>
            <p>All prices inclusive of applicable GST.</p>
            <TodayHours />
            <Link to="/vulnerability" className="inline-block pt-1 text-biga-cream/65 hover:text-biga-orange transition-colors">Vulnerability disclosure</Link>
          </div>
        </div>

        {/* Baseline */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="font-display font-black text-6xl sm:text-8xl tracking-tight text-biga-cream/10 leading-none select-none">BIGA</span>
          <p className="text-xs text-biga-cream/60 font-medium sm:text-right">
            © {new Date().getFullYear()} BIGA Artisanal Pizzeria · Gotri Sevasi Road, Vadodara · Whole Roman-style pizzas
          </p>
        </div>
      </div>
    </footer>
  );
}
